import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { NewVehicleRow } from "../vehicles.types";
import { VehicleType } from "@app.automotus.io/components/common";
import { VehicleTypeValue } from "@app.automotus.io/components/common";

interface SubmitResultBannerProps {
  successCount: number;
  failedRows: NewVehicleRow[];
  onDismiss: () => void;
  networkError?: boolean;
}

const ERROR_CODE_LABELS: Record<string, string> = {
  invalid_plate_format: "Invalid license plate format (must be 3–9 alphanumeric characters)",
  invalid_state_code: "Invalid state code",
  invalid_vehicle_type: "Invalid vehicle type",
  invalid_vehicle_category: "Invalid vehicle category",
  duplicate_in_batch: "Duplicate plate in this submission",
  already_on_account: "Vehicle already registered to your account",
  already_registered: "Vehicle already registered to another account",
};

function errorCodeToReason(code: string | null | undefined): string {
  if (!code) {
    return "Registration failed";
  }
  return ERROR_CODE_LABELS[code] ?? "Registration failed";
}

function escapeCsvCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadFailedRowsCSV(failedRows: NewVehicleRow[]) {
  const header = "State,Plate Number,Make,Model,Type,Reason";
  const csvRows = failedRows.map((row) => {
    const typeLabel = row.type ? VehicleType.FromValue(row.type as VehicleTypeValue)?.label ?? row.type : "";
    const reason = errorCodeToReason(row.submitErrorCode);
    return [
      escapeCsvCell(row.stateCode),
      escapeCsvCell(row.licensePlate),
      escapeCsvCell(row.make ?? ""),
      escapeCsvCell(row.model ?? ""),
      escapeCsvCell(typeLabel),
      escapeCsvCell(reason),
    ].join(",");
  });
  const csv = [header, ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "failed_vehicles.csv";
  a.click();
  // Defer revocation so the browser has time to start the download before the blob URL is freed.
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export const SubmitResultBanner: React.FC<SubmitResultBannerProps> = ({
  successCount,
  failedRows,
  onDismiss,
  networkError,
}) => {
  if (networkError) {
    const partialMessage =
      successCount > 0
        ? ` ${successCount} vehicle${successCount !== 1 ? "s" : ""} ${
            successCount !== 1 ? "were" : "was"
          } registered before the error. Please retry the remaining ones.`
        : " Please try again later.";
    return (
      <Alert severity="error" onClose={onDismiss} sx={{ mb: 2 }}>
        A network error occurred.{partialMessage}
      </Alert>
    );
  }

  const allFailed = successCount === 0 && failedRows.length > 0;
  const someFailed = failedRows.length > 0;

  const severity = allFailed ? "error" : someFailed ? "warning" : "success";

  const message = allFailed
    ? `No vehicles were added. ${failedRows.length} failed.`
    : someFailed
    ? `${successCount} vehicle${successCount !== 1 ? "s" : ""} added. ${failedRows.length} failed.`
    : `${successCount} vehicle${successCount !== 1 ? "s" : ""} added successfully.`;

  return (
    <Alert
      severity={severity}
      onClose={onDismiss}
      action={
        someFailed ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" color="inherit" onClick={() => downloadFailedRowsCSV(failedRows)}>
              Download failures as CSV
            </Button>
          </Stack>
        ) : undefined
      }
      sx={{ mb: 2 }}
    >
      {message}
    </Alert>
  );
};

export default SubmitResultBanner;
